// Copyright (c) StrangeBee 2023

import RouteBuilder from '@Services/RouteBuilder';
import {
    AlertController,
    AlertInput,
    CustomFieldParams,
    AlertOutput,
    CaseCustomField,
    CaseOutput,
    UserOutput,
} from 'thehive-sdk';

import { convertCustomFields } from '@Helpers/customField';
import { queryClient } from '@Client';

export interface AlertEventList {
    onEdit: (alertData: Partial<AlertInput>, alertId: string, forceToStart?: boolean) => Promise<void | AlertOutput>;
    onBulkEdit: (
        caseData: Partial<AlertInput>,
        caseIds: string[],
        forceToStart?: boolean,
    ) => Promise<void | AlertInput>;
    onFollow: (alertId: string) => void;
    onImport: (alertId: string, caseId: string) => Promise<CaseOutput | void>;
    onMultipleFollow: (alertIds: string[]) => Promise<void>;
    onMultipleUnfollow: (alertIds: string[]) => Promise<void>;
    onUnfollow: (alertId: string) => void;
    onUnlinkFromCase: (alertId: string, caseId: string) => void;
    onDelete: (alertId: string) => void;
    onBulkDelete: (alertIds: string[]) => Promise<void>;
    onMergeSeveralIntoCase: (alertIds: string[], caseId: string) => Promise<void | CaseOutput>;
    customField: { onSet: (caseId: string, customFieldData: CustomFieldParams) => Promise<AlertOutput> };
}

interface AlertEventListParams {
    locale: (label: string) => string;
    controller: AlertController;
    onSuccess: (count?: boolean, message?: string) => void;
    onError: (label?: string) => void;
    history: any;
}

export const computeEventList = ({
    controller,
    locale,
    onError,
    onSuccess,
    history,
}: AlertEventListParams): AlertEventList => ({
    onEdit: (alertData, alertId, forceToStart = false) => {
        const currentUser = queryClient.getQueryData<UserOutput>(['currentUser']);
        const opts = forceToStart && !alertData.status ? { status: 'InProgress', assignee: currentUser?.login } : {};

        return controller
            .edit(
                Object.entries({ ...alertData, ...opts }).reduce(
                    (acc, [key, value]) =>
                        key === 'customFields'
                            ? { ...acc, customFields: convertCustomFields(value as CaseCustomField[]) }
                            : { ...acc, [key]: value },
                    {},
                ),
                alertId,
            )
            .then(() => onSuccess(false, locale('notification.alert.edit')))
            .catch(onError);
    },
    onBulkEdit: (
        alertData: Partial<AlertInput>,
        alertIds: string[],
        forceToStart = false,
    ): Promise<void | AlertInput> => {
        const currentUser = queryClient.getQueryData<UserOutput>(['currentUser']);
        const opts = forceToStart && !alertData.status ? { status: 'InProgress', assignee: currentUser?.login } : {};

        return controller
            .bulkEdit<Partial<AlertInput>, void>({ ...alertData, ...opts }, alertIds)
            .then((data) => {
                return data;
            })
            .catch(onError);
    },
    onImport: (alertId, caseId) =>
        controller
            .import(alertId, caseId)
            .then((data) => data)
            .catch(onError),
    onMultipleFollow: (alertIds) =>
        controller
            .multipleFollow(alertIds)
            .then(() => onSuccess(false, locale('notification.alert.follow')))
            .catch(onError),
    onMultipleUnfollow: (alertIds) =>
        controller
            .multipleUnfollow(alertIds)
            .then(() => onSuccess(false, locale('notification.alert.unfollow')))
            .catch(onError),
    onFollow: (alertId: string): void => {
        controller
            .follow(alertId)
            .then(() => onSuccess(false, locale('notification.alert.follow')))
            .catch(onError);
    },
    onUnfollow: (alertId: string): void => {
        controller
            .unFollow(alertId)
            .then(() => onSuccess(false, locale('notification.alert.unfollow')))
            .catch(onError);
    },
    onDelete: (alertId: string): void => {
        controller
            .delete(alertId)
            .then(() => {
                onSuccess(false, locale('notification.alert.delete'));
                history.push(RouteBuilder.compute('alerts.default'));
            })
            .catch(onError);
    },
    onBulkDelete: (alertIds: string[]): Promise<void> =>
        controller
            .bulkDelete(alertIds)
            .then(() => {
                onSuccess(false, locale('notification.alert.delete'));
            })
            .catch(onError),
    onMergeSeveralIntoCase: (alertIds, caseId): Promise<void | CaseOutput> =>
        controller
            .mergeSeveralIntoCase({ alertIds, caseId })
            .then((data) => {
                onSuccess?.(false, locale('notification.alert.create-case-several'));
                return data;
            })
            .catch(onError),
    customField: {
        onSet: (caseId: string, dataCustomField): Promise<AlertOutput> =>
            controller.setCustomField(dataCustomField as CustomFieldParams, caseId),
    },
    onUnlinkFromCase: (alertId, caseId) =>
        controller
            .unlinkFromCase(alertId, caseId)
            .then(() => onSuccess(true, 'Alert successfully unlinked from case!'))
            .catch(onError),
});
